<template lang="pug">
TimeEntriesCalendarEventPopover(
  :time-entry="timeEntry"
  @open-dialog="onOpenDialog"
)
  template(#trigger)
    .fc-content
      .fc-time
        span {{ formattedRange }}
      .fc-title
        span.mrt-icon-wrapper.mr-5(
          v-if="event.extendedProps.manualTimeRequest"
          title="This manual time request needs approval"
        )
          i.hi.hi-warning.text-warning-orange
        | {{ event.title }}
      .fc-task(v-if="event.extendedProps.task") {{ event.extendedProps.task }}
</template>
<script>
import { formatDatetimeRange } from '~/utils/time_utils';
import TimeEntriesCalendarEventPopover from './calendar/TimeEntriesCalendarEventPopover.vue';

// type → the DOM event the corresponding wired dialog listens for on the turbo-frame.
const DIALOG_EVENTS = {
  edit: 'open-time-entry-edit',
  split: 'open-time-entry-split',
  delete: 'time-entry-delete:open',
};

export default {
  name: 'MemberPanelEventPopover',
  components: {
    TimeEntriesCalendarEventPopover,
  },
  // The reused legacy popover injects `calendarStore` (normally the Vuex TimeEntriesStore). We have no
  // store on the member-panels page, so provide a minimal shim — the popover only reads
  // getters.showLocked(timeEntry) and state.prevent_own_time_deletion.
  provide() {
    return {
      calendarStore: {
        getters: { showLocked: (timeEntry) => timeEntry.actionsLocked },
        state: {
          prevent_own_time_deletion: this.preventOwnTimeDeletion || Boolean(this.event.extendedProps.deleteLocked),
        },
      },
    };
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
    timeZone: {
      type: String,
      default: '',
    },
    preventOwnTimeDeletion: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    startTime() {
      return this.inZone(this.event.start);
    },
    stopTime() {
      return this.inZone(this.event.end);
    },
    formattedRange() {
      return formatDatetimeRange(this.startTime, this.stopTime, { style: 'time' });
    },
    // The shape the legacy popover expects, built from our server-emitted extendedProps.
    timeEntry() {
      const props = this.event.extendedProps;

      return {
        id: props.dialogPayload.id,
        project: { text: this.event.title },
        organization: { text: props.org },
        task: props.task ? { text: props.task } : null,
        start_time: this.startTime,
        stop_time: this.stopTime,
        tracked: props.tracked,
        manual: props.manual,
        work_break: props.workBreak,
        billable: props.billable,
        manual_time_request: props.manualTimeRequest,
        editDisabled: props.editLocked,
        modifiableTooltip: null,
        reasons: props.reasons || [],
        allReasons: props.lockReasons || [],
        actionsLocked: props.actionsLocked,
      };
    },
  },
  mounted() {
    // AppPopover teleports its body to <body>, so capture the enclosing turbo-frame from the trigger
    // (which stays inside the frame) now. The wired edit/split/delete dialogs listen for the open
    // events on that frame.
    this.frame = this.$el.closest('turbo-frame');
  },
  methods: {
    inZone(date) {
      return this.timeZone ? moment(date).tz(this.timeZone) : moment(date);
    },
    onOpenDialog({ type }) {
      const eventName = DIALOG_EVENTS[type];

      if (!eventName || !this.frame) {
        return;
      }

      this.frame.dispatchEvent(
        new CustomEvent(eventName, { detail: { timeEntry: this.event.extendedProps.dialogPayload } }),
      );
    },
  },
};
</script>
